import * as A from 'astronomy-engine';
import { coversPlace } from '../live/darksites';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { services } from '../app';
import { activeShowers, hourlyRate, upcomingPeaks } from '../astro/meteors';
import { moonPhaseName, noctilucentWindows, observingNight, riseTransitSet } from '../astro/solar';
import { upcomingEvents, type SkyEvent } from '../astro/events';
import { calendarFocus, calendarPlace, comingUp, tzOf, whenLabel } from '../live/skycalendar';
import { eventsSoon } from '../live/skyevents';
import { Instrument, Sight, bestSights } from '../astro/sights';
import { DEG } from '../lib/math';
import { AuroraChance, CloudHour, auroraChance, auroraNearby, auroraNow, clouds, inUK, kp, kpNeeded, loadClouds, loadKp, loadOvation, ovation } from '../live/conditions';
import { SkyHour, ensembleSources, loadSkyForecast, scoreNight, skyHours } from '../live/skyforecast';
import { SkyConditions, aPct, clearLine, limitText } from './SkyConditions';
import { WORKER_FAILED, displayName, isDockedModule, satStatus, satTimeProblem } from '../live/satellites';
import { FRESH_ORBIT_AGE } from '../live/orbitAge';
import { eyeSqm } from '../live/skycheck';
import { neoList } from '../live/smallbodies';
import type { Pass, TrainPasses } from '../live/satellites';
import { eyeLimit } from '../render/nakedeye';
import { bestView, loadJSON, panel, place, previewFrom, saveJSON, selection, Selection, selKey, setSimTime, settings, simNow, timeLive, viewMode } from '../state';
import { Segmented, Sheet } from './chrome';
import { brightnessWords, compassPoint, directionWords, fmtDate, fmtDay, fmtTime } from './format';
import { Icon } from './icons';
import { nights, sightingName } from '../live/nights';
import { crescentDay, hijriNow, saudiStarts } from '../live/saudi';
import { fmtHijri, fmtHijriShort } from '../astro/hijri';
import { CrescentGuide } from './Crescent';
import { WhiteDaysNote } from './IslamNotes';
import { openIslam } from './IslamPanel';
import { nightId } from '../live/nightsCore';
import { downloadPassCalendar } from './calendar';
import { NightChart } from './NightChart';
import { unlockNarration } from '../audio/narrator';
import { MoonGlyph, crescentPhase } from './MoonGlyph';
import { RamadanView } from './RamadanView';

const auroraText = (c: AuroraChance, pole: string) =>
  ({
    none: 'Very unlikely tonight',
    camera: `Possible on long-exposure photos, low in the ${pole}`,
    possible: `Possible low in the ${pole}`,
    likely: `Good chance, perhaps overhead: look ${pole} away from lights`,
  })[c];

/** The same chances for the three hours under way, and the colour of the dot beside them. */
const auroraNowText = (c: AuroraChance, pole: string) =>
  ({
    none: 'Too quiet for aurora here',
    camera: `Perhaps on long-exposure photos, low in the ${pole}`,
    possible: `Aurora possible low in the ${pole}`,
    likely: `Aurora likely: look ${pole} away from lights`,
  })[c];
const AURORA_DOT: Record<AuroraChance, string> = { none: 'var(--text-3)', camera: 'var(--accent)', possible: 'var(--warn)', likely: 'var(--bad)' };

export function TonightPanel() {
  const s = services.value!;
  const obs = s.engine.frame.observer;
  const now = simNow();
  const pl = place.value;
  const [passes, setPasses] = useState<Pass[] | null>(null);
  const placeKey = `${pl.lat.toFixed(2)},${pl.lon.toFixed(2)}`;
  const [cloudHours, setCloudHours] = useState<CloudHour[] | null>(clouds.peek()?.key === placeKey ? clouds.peek()!.hours : null);
  const [sky, setSky] = useState<SkyHour[] | null>(skyHours.peek()?.key === placeKey ? skyHours.peek()!.hours : null);
  // Settled with nothing: offline, or the forecast service refused.
  const [skyFailed, setSkyFailed] = useState(false);
  const [kpFailed, setKpFailed] = useState(false);
  const [trains, setTrains] = useState<TrainPasses[] | null>(null);
  // Why there are no passes to list, when it is not that none come over (orbits too old, say).
  const [passNote, setPassNote] = useState<string | null>(null);
  const sat = satStatus.value;
  // Drawn again each minute, so the night and its Hijri date (which turns at sunset) move on while the panel is open.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 60e3);
    return () => clearInterval(id);
  }, []);

  // The night in question: tonight, or the one in progress.
  const minute = Math.floor(now.getTime() / 60e3);
  const night = useMemo(() => observingNight(obs, new Date(minute * 60e3)), [obs.latitude, obs.longitude, minute]);
  const from = (night.sunset ?? night.noon).getTime();
  const to = (night.sunrise ?? new Date(from + 12 * 3600e3)).getTime();
  const darkStart = night.astroDusk ?? night.nauticalDusk ?? night.civilDusk;
  const darkEnd = night.astroDawn ?? night.nauticalDawn ?? night.civilDawn;

  useEffect(() => {
    // Answers for a place or night the panel has since moved on from are dropped.
    let live = true;
    const keep = <T,>(f: (v: T) => void) => (v: T) => void (live && f(v));
    const cur = skyHours.peek(), cl = clouds.peek();
    setSky(cur?.key === placeKey ? cur.hours : null);
    setSkyFailed(false);
    setCloudHours(cl?.key === placeKey ? cl.hours : null);
    void s.small.loadNeo();
    void loadClouds(pl).then(keep(setCloudHours));
    void loadSkyForecast(pl, keep(setSky)).then(
      keep((h: SkyHour[] | null) => (h ? setSky(h) : setSkyFailed(true))),
      keep(() => setSkyFailed(true)),
    );
    void loadKp().then((p) => setKpFailed(!p));
    return () => {
      live = false;
    };
  }, [pl.lat, pl.lon, from]);

  // Satellite passes: worked out again for each new catalogue (the built-in one first, then the latest orbits),
  // keeping the last list on show meanwhile; a new place or night starts afresh.
  useEffect(() => {
    setPasses(null);
    setTrains(null);
    setPassNote(null);
  }, [pl.lat, pl.lon, from]);
  useEffect(() => {
    if (!sat.rev) return; // no orbits yet: they are on their way
    let live = true;
    const keep = <T,>(f: (v: T) => void) => (v: T) => void (live && f(v));
    const start = Math.max(now.getTime(), from - 3600e3);
    const hours = Math.max(1, (to - start) / 3600e3);
    // Orbit data predicts well only for a couple of weeks either side of when it was measured.
    const problem = satTimeProblem(new Date(start));
    if (problem) {
      setPasses([]);
      setTrains([]);
      setPassNote(problem === 'far' ? 'Satellite passes can only be predicted for about two weeks either side of today.' : 'The saved satellite orbits are too old to predict passes. Connect to the internet to update them.');
      return;
    }
    const failed = () => {
      setPasses([]);
      setPassNote('Ahillah could not work out tonight’s passes just now.');
    };
    s.sats.passes('notable', new Date(start), hours, 3.5).then(keep((p: Pass[]) => {
      // Space stations first; skip docked modules (they share the station's pass) and brief, low passes.
      const station = (id: number) => id === 25544 || id === 48274;
      const list = p.filter((x) => {
        if (!x.visStart || !x.visEnd) return false;
        const meta = s.sats.meta.get(x.id);
        if (meta && isDockedModule(meta)) return false;
        return station(x.id) || (x.peakEl >= 15 && x.visEnd - x.visStart >= 2 * 60e3);
      });
      list.sort((a, b) => Number(station(b.id)) - Number(station(a.id)) || a.visStart! - b.visStart!);
      setPasses(list);
      setPassNote(null);
    }), keep(failed));
    if (settings.peek().starlink) s.sats.trains(new Date(start), hours).then(keep(setTrains), keep(() => setTrains([])));
    return () => {
      live = false;
    };
  }, [pl.lat, pl.lon, from, sat.rev]);

  // Moon.
  const moonPhase = A.MoonPhase(now);
  const moonIll = A.Illumination(A.Body.Moon, now).phase_fraction;
  // The Moon's pass that matters tonight: if it is up at sunset, when it rose and when it sets;
  // otherwise when it rises and then sets.
  const moonRs = useMemo(() => riseTransitSet(A.Body.Moon, obs, new Date(from)), [from, obs.latitude, obs.longitude]);
  const moonRise = moonRs.rise, moonSet = moonRs.set;
  // On the 29th, the 30th and the 1st, just "Crescent" (src/live/saudi.ts): so too when tonight, or the rising or
  // setting the row would give, falls on one of them (on the 28th, tonight is the 29th's).
  const crescent = [now.getTime(), from + 60e3, moonRise?.getTime(), moonSet?.getTime()].some((t) => t != null && crescentDay(t, pl));

  // Sky conditions: the night's score and what holds it back.
  const set = settings.value;
  const darkFrom = (darkStart ?? new Date(from)).getTime(), darkTo = (darkEnd ?? new Date(to)).getTime();
  // The sky's own light pollution as the eye found it (the darkness check), else the map's.
  const sqmEye = eyeSqm(place.value, set);
  const nightScore = useMemo(
    () => scoreNight(sky ?? [], obs, from - 1800e3, to, set.bortle, sqmEye, now.getTime()),
    [sky, from, to, set.bortle, sqmEye, Math.floor(now.getTime() / 600e3)],
  );
  const best = nightScore.best;
  const ens = ensembleSources(pl.lat, pl.lon);
  const forecast = skyHours.value?.key === placeKey ? skyHours.value : null;
  const ensOk = forecast ? forecast.parts.ensemble : true;
  const score = best ? Math.round(best.score) : null;
  const neverDark = !night.astroDusk;

  // Planets worth looking at tonight: above 10° at some point during darkness.
  const planets = useMemo(() => {
    const out: { id: string; bestT: number; alt: number; az: number; mag: number }[] = [];
    for (const id of ['Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune'] as const) {
      let best = { t: 0, alt: -90, az: 0 };
      for (let t = from; t <= to; t += 20 * 60e3) {
        const sunAlt = A.Horizon(t2d(t), obs, ...eqHD(A.Body.Sun, t, obs), 'normal').altitude;
        if (sunAlt > -6) continue;
        const [ra, dec] = eqHD(A.Body[id], t, obs);
        const h = A.Horizon(t2d(t), obs, ra, dec, 'normal');
        if (h.altitude > best.alt) best = { t, alt: h.altitude, az: h.azimuth };
      }
      const mag = A.Illumination(A.Body[id], t2d(best.t || from)).mag;
      if (best.alt > 10 && mag < 6.5) out.push({ id, bestT: best.t, alt: best.alt, az: best.az, mag });
    }
    return out.sort((a, b) => a.mag - b.mag);
  }, [from, obs.latitude, obs.longitude]);

  // Meteors: the eye's limit in a dark sky here, as Naked eye has it (render/nakedeye.ts).
  const nelm = eyeLimit(-30, -30, 0, set.bortle, sqmEye, 0);
  const showers = activeShowers(s.meteors.showers, new Date(Math.max(now.getTime(), from + 5 * 3600e3)), 1.5);
  const peaks = upcomingPeaks(s.meteors.showers, now, 75).filter((p) => (p.shower.zhr ?? 0) >= 10).slice(0, 3);

  // Comets and asteroids.
  const smallBright = useMemo(() => s.small.brightest(new Date(from + 5 * 3600e3), 5), [from, s.small.comets.length]);
  const neos = neoList.value;

  // Notable events over the next two months.
  // Worked out in the events worker, so neither opening the panel nor the sky waits on these sums.
  const [events, setEvents] = useState<ReturnType<typeof upcomingEvents> | null>(null);
  const evHour = Math.floor(now.getTime() / 3600e3);
  // Worked out again when Saudi Arabia's decided starts change, so the crescent's evening moves with them.
  const decided = saudiStarts.value;
  useEffect(
    () => eventsSoon({ lat: obs.latitude, lon: obs.longitude, height: obs.height, tz: pl.timeZone }, simNow().getTime(), 60, s.meteors.showers, settings.peek().bortle, setEvents),
    [obs.latitude, obs.longitude, evHour, decided],
  );

  // A coming event opens the sky calendar at it, for the place on the sky. While another time is on the sky,
  // these are that time's events: go to the moment itself instead.
  const openEvent = (e: SkyEvent) => {
    if (!timeLive.peek()) {
      setSimTime(new Date(e.t), 0);
      if (e.select) selection.value = e.select;
      panel.value = null;
      return;
    }
    if (previewFrom.peek()) calendarPlace.value = pl;
    calendarFocus.value = e.id;
    panel.value = 'calendar';
  };

  // Noctilucent clouds, in season.
  const nlc = useMemo(() => noctilucentWindows(obs, night.noon), [obs.latitude, obs.longitude, night.noon.getTime()]);

  // Aurora.
  const k = kp.value?.points ?? [];
  const tonightKp = k.filter((p) => p.t >= from - 3 * 3600e3 && p.t <= to);
  const maxKp = tonightKp.length ? Math.max(...tonightKp.map((p) => p.kp)) : null;
  const need = kpNeeded(pl.lat, pl.lon);
  const chance = maxKp != null ? auroraChance(maxKp, need) : null;
  const pole = pl.lat >= 0 ? 'north' : 'south';
  // The clearest dark hour tonight, to say when cloud will hide a display.
  const darkClear = (sky ?? []).filter((h) => h.t >= darkFrom && h.t < darkTo && h.pClear != null).map((h) => h.pClear!);
  const bestClear = darkClear.length ? Math.max(...darkClear) : null;
  // NOAA's nowcast looks 30 to 90 minutes ahead: only worth fetching live, in the dark, when aurora is plausible.
  const liveNow = Math.abs(now.getTime() - Date.now()) < 30 * 60e3;
  // NOAA's Kp for the three hours under way, while the sky shows now.
  const kpNow = liveNow ? auroraNow(k, Date.now(), need) : null;
  const darkNow = A.Horizon(now, obs, ...eqHD(A.Body.Sun, now.getTime(), obs), 'normal').altitude < -6;
  const plausible = (maxKp != null && maxKp >= need - 3) || (!!kpNow && kpNow.chance !== 'none') || need <= 3;
  const wantOvation = liveNow && darkNow && plausible;
  useEffect(() => {
    if (wantOvation) void loadOvation();
  }, [wantOvation]);
  const ov = wantOvation && ovation.value ? auroraNearby(ovation.value.grid, pl.lat, pl.lon) : null;

  // Open the card for the sky as it is now; the best time is offered there, not jumped to.
  const openAt = (sel: Selection, t: number) => {
    bestView.value = { key: selKey(sel), t };
    selection.value = sel;
    panel.value = null;
  };
  const showPass = (p: Pass) => {
    setSimTime(new Date((p.visStart ?? p.rise) - 30e3), 1);
    selection.value = { kind: 'satellite', id: p.id };
    panel.value = null;
    if (viewMode.peek() === 'manual') {
      const az = p.visStartAz * DEG, el = Math.max(p.visStartEl, 15) * DEG;
      s.engine.flyTo([Math.cos(el) * Math.sin(az), Math.cos(el) * Math.cos(az), Math.sin(el)], 110, 1.2);
    }
  };

  const tone = score == null ? 'ok' : score >= 7 ? 'good' : score >= 4 ? 'ok' : 'poor';
  const sunUp = (t: number) => A.Horizon(t2d(t), obs, ...eqHD(A.Body.Sun, t, obs), 'normal').altitude > 0;
  const verdict = best
    ? score! >= 7 ? 'A good night for stargazing' : score! >= 4 ? 'A fair night for stargazing' : 'A poor night for stargazing'
    : !sky
      ? skyFailed ? 'The forecast is unavailable' : 'Checking the forecast…'
      : !night.sunset && !night.sunrise && sunUp(from)
        ? 'The Sun does not set tonight'
        : nightScore.hours.length
          ? 'The dark hours tonight have passed'
          : sky.length && from > sky[sky.length - 1].t
            ? 'Too far ahead to forecast'
            : to < Date.now()
              ? 'No forecast kept for past nights'
              : 'The forecast is unavailable';
  const verdictSub = best
    ? `Best ${fmtTime(best.start)}–${fmtTime(best.end)}: ${limitText(best.limits, neverDark)}`
    : sky && sky.length && from > sky[sky.length - 1].t
      ? 'Sky forecasts reach about three days ahead'
      : !sky && skyFailed
        ? navigator.onLine ? 'The forecast is unavailable just now. Try again in a few minutes.' : 'You are offline. The rest of Tonight still works.'
        : '';
  // The clear-sky outlook in one line, for the closed Conditions row.
  const darkHours = (sky ?? []).filter((h) => h.t >= darkFrom - 1800e3 && h.t <= darkTo);
  const nightHours = (sky ?? []).filter((h) => h.t >= from - 1800e3 && h.t <= to);
  const outlook = sky ? (darkHours.length || nightHours.length ? clearLine(darkHours.length ? darkHours : nightHours) : 'No forecast for these hours') : skyFailed ? 'Forecast unavailable' : 'Checking the forecast…';
  // Aurora joins the main list only when there is a real chance of seeing it.
  const auroraHot = chance === 'possible' || chance === 'likely' || (inUK(pl.lat, pl.lon) && !!kpNow && (kpNow.chance === 'possible' || kpNow.chance === 'likely'));
  const moonRow = (
    <button class="result" onClick={() => { selection.value = { kind: 'body', id: 'Moon' }; panel.value = null; }}>
      <span class="moon-glyph"><MoonGlyph phase={crescent ? crescentPhase(moonPhase) : moonPhase} size={24} /></span>
      <span class="result-text">
        <span class="result-label">Moon{!crescent && <span class="muted"> · {Math.round(moonIll * 100)}% lit</span>}</span>
        <span class="result-sub tnum">{crescent ? 'Crescent' : moonPhaseName(moonPhase)}{!crescent && (moonRise || moonSet) ? ` · ${[moonRise && `rises ${fmtTime(moonRise)}`, moonSet && `sets ${fmtTime(moonSet)}`].filter(Boolean).join(', ')}` : ''}</span>
      </span>
      <Icon name="chevron" size={16} />
    </button>
  );
  const auroraCard = (
    <div class="card wide">
      <div class="card-title"><Icon name="aurora" size={14} /> Aurora</div>
      <div class="card-main">
        {chance
          ? auroraText(chance, pole)
          : kpFailed
            ? 'Space weather data is unavailable right now'
            : k.length
              ? from > k[k.length - 1].t
                ? 'Aurora forecasts reach only about three days ahead'
                : 'No space-weather record for this date'
              : 'Checking space weather…'}
      </div>
      {maxKp != null && (
        <div class="card-sub">
          Geomagnetic activity up to Kp {maxKp.toFixed(1)} tonight;{' '}
          {need > 9 ? 'aurora almost never reaches this far from the magnetic pole.' : `about Kp ${Math.max(1, Math.ceil(need))} brings it overhead here.`}
        </div>
      )}
      {chance && chance !== 'none' && bestClear != null && bestClear < 0.3 && (
        <div class="card-sub">Cloud will probably hide it: {bestClear < 0.05 ? 'almost no' : `at best ${aPct(bestClear)}`} chance of a clear sky after dark.</div>
      )}
      {ov && (
        <div class="card-sub">
          Next hour, from NOAA’s OVATION model: {ov.overhead}% chance of aurora overhead, {ov.horizon}% low in the {pole}.
        </div>
      )}
      {inUK(pl.lat, pl.lon) && kpNow && (
        <div class="aurora-now">
          <span class="aurora-dot" style={{ background: AURORA_DOT[kpNow.chance] }} />
          <span>
            <strong>Now: Kp {kpNow.kp.toFixed(1)}</strong> ({kpNow.kind === 'observed' ? 'measured' : kpNow.kind === 'estimated' ? 'NOAA’s estimate' : 'NOAA’s forecast'}). {auroraNowText(kpNow.chance, pole)}.
          </span>
        </div>
      )}
    </div>
  );

  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Tonight">
      <h2 class="sheet-title">Tonight</h2>
      <p class="sheet-lead">{pl.name} · {fmtDate(night.sunset ?? now)} · {fmtHijri(hijriNow(now.getTime(), pl))}</p>

      <div class="hero">
        <Gauge score={score} raw={best ? best.score : null} tone={tone} />
        <div class="hero-text">
          <div class="hero-verdict">{verdict}</div>
          {verdictSub && <div class="hero-sub tnum">{verdictSub}</div>}
        </div>
      </div>

      <NightRibbon crescent={crescent} obs={obs} from={from} to={to} now={now.getTime()} best={best ? [best.start, best.end] : null} darkStart={darkStart} darkEnd={darkEnd} sunset={night.sunset} sunrise={night.sunrise} />

      <div class="tonight-actions">
        <button class="btn tour-btn" onClick={() => { unlockNarration(); panel.value = 'tour'; }}>
          <Icon name="play" size={16} /> Guided tour
        </button>
        <button class="btn-ghost tour-btn" onClick={() => { selection.value = null; panel.value = 'hunt'; }}>
          <Icon name="target" size={17} /> Sky Hunt
        </button>
      </div>

      <RamadanView now={now.getTime()} pl={pl} />
      <CrescentGuide />
      <WhiteDaysNote h={hijriNow(now.getTime(), pl)} />

      <YourNightsRow />

      {/* "The stars in Islam" near the top of Tonight (BUILD-SPEC item 35). */}
      <button class="go-card" onClick={() => { selection.value = null; openIslam(); }}>
        <span class="result-icon"><Icon name="book" size={20} /></span>
        <span class="result-text">
          <span class="result-label">The stars in Islam</span>
          <span class="result-sub">What the Quran and the Sunnah say about the sky</span>
        </span>
        <Icon name="chevron" size={16} />
      </button>

      {auroraHot && <><h3 class="section-title">Aurora</h3>{auroraCard}</>}

      <h3 class="section-title">Up tonight</h3>
      <div class="results">
        {moonRow}
        {planets.map((p) => (
          <button class="result" key={p.id} onClick={() => openAt({ kind: 'body', id: p.id }, p.bestT)}>
            <span class="result-icon"><Icon name="planet" size={20} /></span>
            <span class="result-text">
              <span class="result-label">{p.id}</span>
              <span class="result-sub">Best at {fmtTime(p.bestT)}, {Math.round(p.alt)}° up in the {directionWords(p.az)} · {p.mag > 5.5 ? 'binoculars' : brightnessWords(p.mag)}</span>
            </span>
            <Icon name="chevron" size={16} />
          </button>
        ))}
        {!planets.length && <div class="empty">No bright planets are well placed tonight.</div>}
      </div>

      <BestSights from={darkStart?.getTime() ?? from} to={darkEnd?.getTime() ?? to} openAt={openAt} />

      <h3 class="section-title">Satellites</h3>
      <div class="results">
        {!passes && <div class="empty">{!sat.rev && sat.error ? (sat.error === WORKER_FAILED ? 'Ahillah could not work out tonight’s passes just now.' : 'Satellite orbits are not available offline. Connect to the internet to load them.') : 'Working out tonight’s passes…'}</div>}
        {passes && !passes.length && <div class="empty">{passNote ?? 'No bright satellite passes during darkness tonight.'}</div>}
        {passes?.slice(0, 12).map((p) => {
          const meta = s.sats.meta.get(p.id);
          const name = meta ? displayName(meta) : p.name;
          return (
            <div class="result" key={`${p.id}-${p.rise}`}>
              <button class="result-main" onClick={() => showPass(p)}>
                <span class="result-icon"><Icon name="satellite" size={20} /></span>
                <span class="result-text">
                  <span class="result-label">{name} <span class="muted tnum">{fmtTime(p.visStart!)}</span></span>
                  <span class="result-sub">{compassPoint(p.visStartAz)} → {compassPoint(p.visEndAz)} · up to {Math.round(p.peakEl)}° · {Math.max(1, Math.round((p.visEnd! - p.visStart!) / 60000))} min{p.minMag != null ? ` · ${brightnessWords(p.minMag)}` : ''}</span>
                </span>
              </button>
              <button class="icon-btn" aria-label={`Add the ${name} pass at ${fmtTime(p.visStart!)} to your calendar`} onClick={() => downloadPassCalendar(name, p)}><Icon name="bell" size={19} /></button>
            </div>
          );
        })}
        {/* An orbit measured more than three days before its pass can put the times a few minutes out. */}
        <OrbitNote gap={orbitGap(passes, s.sats.meta)} />
      </div>

      {settings.value.starlink && trains && (
        <>
          <h3 class="section-title">Starlink trains</h3>
          <div class="results">
            {!trains.some((t) => t.passes.length) && (
              <div class="empty">
                {trains.length
                  ? 'A recent Starlink launch is still in a line, but it does not pass over in darkness tonight.'
                  : 'No Starlink trains expected. After each launch, the new satellites travel in a line of lights for a week or two.'}
              </div>
            )}
            {trains.flatMap((t) => t.passes.map((p) => ({ t, p }))).sort((a, b) => a.p.visStart! - b.p.visStart!).slice(0, 4).map(({ t, p }) => (
              <button class="result" key={`${t.group}-${p.rise}`} onClick={() => showPass(p)}>
                <span class="result-icon"><Icon name="satellite" size={20} /></span>
                <span class="result-text">
                  <span class="result-label">A line of up to {t.count} satellites <span class="muted tnum">{fmtTime(p.visStart!)}</span></span>
                  <span class="result-sub">{compassPoint(p.visStartAz)} → {compassPoint(p.visEndAz)} · up to {Math.round(p.peakEl)}° · launched around {fmtDay(t.launched)}, about {t.altKm} km up. They follow one another along the same path over a few minutes.</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      <h3 class="section-title">Meteors</h3>
      <div class="results">
        {showers.map((a) => {
          const rate = hourlyRate(a.zhr, 60, a.shower.r, nelm);
          return (
            <button class="result" key={a.shower.code} onClick={() => { selection.value = { kind: 'shower', code: a.shower.code }; panel.value = null; }}>
              <span class="result-icon"><Icon name="meteor" size={20} /></span>
              <span class="result-text">
                <span class="result-label">{a.shower.name}</span>
                <span class="result-sub">Active · up to about {Math.max(1, Math.round(rate))} an hour from here when the radiant is high</span>
              </span>
              <Icon name="chevron" size={16} />
            </button>
          );
        })}
        {peaks.map((p) => (
          <button class="result" key={`peak-${p.shower.code}`} onClick={() => { selection.value = { kind: 'shower', code: p.shower.code }; panel.value = null; }}>
            <span class="result-icon"><Icon name="calendar" size={20} /></span>
            <span class="result-text">
              <span class="result-label">{p.shower.name} peak</span>
              <span class="result-sub">{fmtDate(p.date)} · up to {p.shower.zhr} an hour under ideal skies</span>
            </span>
            <Icon name="chevron" size={16} />
          </button>
        ))}
        <div class="empty">Stray “sporadic” meteors add a few an hour on any dark night, more before dawn.</div>
      </div>

      <h3 class="section-title">Conditions</h3>
      <details class="more">
        <summary>
          <span class="result-icon"><Icon name="cloud" size={20} /></span>
          <span class="result-text">
            <span class="result-label">Sky conditions</span>
            <span class="result-sub">{outlook}</span>
          </span>
          <Icon name="chevron" size={16} />
        </summary>
        <div class="more-body">
          {sky ? (
            <SkyConditions hours={sky} score={nightScore} from={from} to={to} darkFrom={darkFrom} darkTo={darkTo} now={now.getTime()} runs={ensOk ? ens.runs : 0} model={clouds.value?.model} />
          ) : (
            <div class="card-main">{outlook}</div>
          )}
          <details class="sources">
            <summary>About this forecast</summary>
            <div class="fine">
              Forecast: {clouds.value?.model ? `${clouds.value.model} (CC BY-SA 4.0)` : 'weather models'} via <a href="https://open-meteo.com/" target="_blank" rel="noopener">Open-Meteo</a>. Clear-sky
              chance from {ensOk ? `the ${ens.centres} ensembles` : forecast?.pending ? 'the main forecast alone for now (the ensembles are still loading)' : 'the main forecast alone (the ensembles did not load)'}; haze from Copernicus CAMS aerosols; seeing worked out from NOAA GFS with the
              AXP turbulence model, calibrated against telescope measurements at ESO Paranal and Maunakea. Seeing is for the open air
              straight overhead: it is worse lower down (about 1.5 times at 30° up), warm roofs and telescopes add blur of their own, and it
              is a guide to the kind of night more than an hour-by-hour promise.
            </div>
          </details>
        </div>
      </details>
      <details class="more">
        <summary>
          <span class="result-icon"><Icon name="moon" size={20} /></span>
          <span class="result-text">
            <span class="result-label">The night at a glance</span>
            <span class="result-sub">{crescent ? 'How high the planets climb, hour by hour' : 'How high the Moon and planets climb, hour by hour'}</span>
          </span>
          <Icon name="chevron" size={16} />
        </summary>
        <div class="more-body">
          <NightChart obs={obs} from={from - 3600e3} to={to + 1800e3} now={now.getTime()} clouds={cloudHours} noMoon={crescent} />
          <div class="card-sub">Height above the horizon. The shading shows how dark it gets; the bar along the top shows cloud.</div>
        </div>
      </details>
      {coversPlace(pl) && (
        <button class="go-card" onClick={() => (panel.value = 'go')}>
          <span class="result-icon"><Icon name="pin" size={20} /></span>
          <span class="result-text">
            <span class="result-label">Find darker skies nearby</span>
            <span class="result-sub">Public places within driving distance, ranked by darkness, tonight’s cloud and the Moon</span>
          </span>
          <Icon name="chevron" size={16} />
        </button>
      )}

      <h3 class="section-title">Coming up</h3>
      <div class="results">
        {!events && <div class="empty">Working out the coming weeks…</div>}
        {comingUp(events ?? [], now.getTime()).map((e) => (
          <button class={`result event w${e.weight}`} key={e.id} onClick={() => openEvent(e)}>
            <span class="result-icon"><Icon name={e.icon} size={20} /></span>
            <span class="result-text">
              <span class="result-label">{e.title}</span>
              <span class="result-sub tnum">{whenLabel(e, tzOf(pl))} · {fmtHijriShort(hijriNow(e.kind === 'crescent' ? e.t - 60e3 : e.t, pl))}</span>
            </span>
            <Icon name="chevron" size={16} />
          </button>
        ))}
        <button class="show-more" onClick={() => { if (previewFrom.peek()) calendarPlace.value = pl; panel.value = 'calendar'; }}><Icon name="calendar" size={16} /> The year ahead: sky calendar</button>
      </div>

      {/* Lessons, the Gallery and the rest of Ahillah are in More, the dock's fifth button (DECISIONS 108). */}
      <h3 class="section-title">Also in the sky</h3>
      <details class="more">
        <summary>
          <span class="result-icon"><Icon name="aurora" size={20} /></span>
          <span class="result-text">
            <span class="result-label">{auroraHot ? 'Comets, asteroids and more' : 'Aurora, comets and more'}</span>
            <span class="result-sub">{auroraHot ? 'Comets, passing asteroids and seasonal sights' : chance ? `Aurora: ${auroraText(chance, pole).toLowerCase()}` : 'Aurora, comets, passing asteroids and seasonal sights'}</span>
          </span>
          <Icon name="chevron" size={16} />
        </summary>
        <div class="more-body">
          {!auroraHot && auroraCard}
          {nlc && (nlc.evening || nlc.morning) && (
            <div class="card wide">
              <div class="card-title"><Icon name="cloud" size={14} /> Noctilucent clouds</div>
              <div class="card-main">It is the season for them</div>
              <div class="card-sub tnum">
                Look low in the {pl.lat > 0 ? 'north' : 'south'} for thin, shining electric-blue clouds
                {nlc.evening ? ` ${nlc.morning ? 'after dusk, ' : ''}${fmtTime(nlc.evening[0])}–${fmtTime(nlc.evening[1])}` : ''}
                {nlc.morning ? `${nlc.evening ? ', and before dawn, ' : ' before dawn, '}${fmtTime(nlc.morning[0])}–${fmtTime(nlc.morning[1])}` : ''}. They are the highest clouds on Earth, about 80 km up, still lit by the Sun long after sunset. Not every clear night has them.
              </div>
            </div>
          )}
          <div class="card wide">
            <div class="card-title"><Icon name="meteor" size={14} /> Comets and asteroids</div>
            {!smallBright.some((x) => x.body.kind === 'comet' && x.st.mag < 9) && <div class="card-sub">No comet is bright enough for binoculars at the moment. Ahillah checks the Minor Planet Center daily, so a bright one will appear here.</div>}
            <div class="results">
              {smallBright.map(({ body, st }) => (
                <button class="result" key={body.name} onClick={() => { selection.value = { kind: 'small', name: body.name }; panel.value = null; }}>
                  <span class="result-icon"><Icon name={body.kind === 'comet' ? 'meteor' : 'planet'} size={20} /></span>
                  <span class="result-text">
                    <span class="result-label">{body.name}</span>
                    <span class="result-sub">{body.kind === 'comet' ? 'Comet' : 'Asteroid'} · {st.mag < 5.5 ? 'visible by eye from a dark site' : st.mag < 9 ? 'binoculars' : st.mag < 13 ? 'small telescope' : 'large telescope'}</span>
                  </span>
                  <Icon name="chevron" size={16} />
                </button>
              ))}
            </div>
          </div>
          {neos && neos.length > 0 && (
            <div class="card wide">
              <div class="card-title"><Icon name="planet" size={14} /> Asteroids passing Earth this month</div>
              <div class="list">
                {neos.slice(0, 5).map((n) => {
                  const ld = n.distAu * 389.17;
                  const sizeM = (1329 / Math.sqrt(0.14)) * Math.pow(10, -n.h / 5) * 1000;
                  return (
                    <div class="list-row" key={n.des + n.date}>
                      <span>{n.des} <span class="muted">· about {sizeM >= 1000 ? `${(sizeM / 1000).toFixed(1)} km` : `${Math.round(sizeM / 10) * 10} m`}</span></span>
                      <span class="tnum muted">{n.date.slice(5, 11).replace('-', ' ')} · {ld < 10 ? ld.toFixed(1) : Math.round(ld)}× Moon distance</span>
                    </div>
                  );
                })}
              </div>
              <div class="card-sub">All pass safely; none are bright enough to see without a large telescope. Data: NASA JPL.</div>
            </div>
          )}
        </div>
      </details>
    </Sheet>
  );
}

/** The night's rating as a ring that fills to its score out of ten. */
/** Tonight's own night, if anything has been kept, and the way to all of them. */
function YourNightsRow() {
  const s = services.value;
  const all = nights.value;
  const home = previewFrom.value?.home ?? place.value;
  const mine = all[nightId(Date.now(), home.lon)];
  const count = Object.keys(all).length;
  const names = mine?.sightings.map((x) => sightingName(s, x)) ?? [];
  const sub = mine
    ? names.length ? `Tonight: ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` and ${names.length - 3} more` : ''}` : 'Tonight is saved'
    : count ? `${count} ${count === 1 ? 'night' : 'nights'} saved` : 'Tick “Seen it” on what you spot, and it’s saved here night by night';
  return (
    <button class="go-card nights-card" onClick={() => { selection.value = null; panel.value = 'nights'; }}>
      <span class="result-icon"><Icon name="nights" size={20} /></span>
      <span class="result-text">
        <span class="result-label">Your nights</span>
        <span class="result-sub">{sub}</span>
      </span>
      <Icon name="chevron" size={16} />
    </button>
  );
}

function Gauge({ score, raw, tone }: { score: number | null; raw: number | null; tone: string }) {
  const R = 28, C = 2 * Math.PI * R;
  // A sliver always shows once there is a rating, so even a poor night has its colour.
  const len = raw == null ? 0 : (Math.max(0.3, Math.min(10, raw)) / 10) * C;
  return (
    <div class={`gauge score-${tone}`} role="img" aria-label={score == null ? 'Not rated yet' : `Rated ${score} out of 10`}>
      <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r={R} fill="none" stroke="rgba(255,255,255,0.09)" stroke-width="5" />
        {len > 0 && (
          <circle class="gauge-arc" cx="32" cy="32" r={R} fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round"
            stroke-dasharray={`${len} ${C}`} transform="rotate(-90 32 32)" style={{ '--len': `${len}` }} />
        )}
      </svg>
      {score != null && <b aria-hidden="true">{score}</b>}
    </div>
  );
}

/** Sky colour for the Sun's altitude: day blue through the twilights to night. */
function skyTone(alt: number): string {
  const stops: [number, [number, number, number]][] = [[6, [104, 146, 206]], [0, [78, 112, 170]], [-6, [48, 66, 118]], [-12, [30, 40, 80]], [-18, [21, 27, 54]]];
  if (alt >= stops[0][0]) return `rgb(${stops[0][1].join(',')})`;
  for (let i = 1; i < stops.length; i++) {
    if (alt >= stops[i][0]) {
      const [a0, c0] = stops[i - 1], [a1, c1] = stops[i];
      const f = (alt - a1) / (a0 - a1);
      return `rgb(${c0.map((c, k) => Math.round(c1[k] + (c - c1[k]) * f)).join(',')})`;
    }
  }
  return `rgb(${stops[stops.length - 1][1].join(',')})`;
}

/** The whole night in one strip: dusk, darkness and dawn, when the Moon is up (not on the 29th, the 30th and the 1st,
 *  which give no rising or setting: src/live/saudi.ts), the best hours and now. */
export function NightRibbon({ obs, from, to, now, best, darkStart, darkEnd, sunset, sunrise, crescent }: {
  obs: A.Observer; from: number; to: number; now: number; best: [number, number] | null; crescent: boolean;
  darkStart: Date | null | undefined; darkEnd: Date | null | undefined; sunset: Date | null | undefined; sunrise: Date | null | undefined;
}) {
  const W = 340, H = 22, top = 10;
  const x = (t: number) => ((t - from) / (to - from)) * W;
  const data = useMemo(() => {
    const n = 48, sun: number[] = [], moon: number[] = [];
    for (let k = 0; k <= n; k++) {
      const t = from + ((to - from) * k) / n;
      sun.push(A.Horizon(t2d(t), obs, ...eqHD(A.Body.Sun, t, obs), 'normal').altitude);
      moon.push(A.Horizon(t2d(t), obs, ...eqHD(A.Body.Moon, t, obs), 'normal').altitude);
    }
    return { sun, moon, n };
  }, [from, to, obs.latitude, obs.longitude]);
  const lit = A.Illumination(A.Body.Moon, t2d((from + to) / 2)).phase_fraction;
  // Runs of time with the Moon above the horizon.
  const moonRuns: [number, number][] = [];
  data.moon.forEach((a, k) => {
    if (a <= 0 || crescent) return;
    const last = moonRuns[moonRuns.length - 1];
    if (last && last[1] === k - 1) last[1] = k;
    else moonRuns.push([k, k]);
  });
  const kx = (k: number) => (k / data.n) * W;
  const id = `rb${Math.round(from / 60e3)}`;
  return (
    <div class="ribbon">
      <svg viewBox={`0 0 ${W} ${top + H + 4}`} role="img" aria-label={`Tonight from sunset to sunrise${best ? `; best ${fmtTime(best[0])} to ${fmtTime(best[1])}` : ''}`}>
        <defs>
          <linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
            {data.sun.map((a, k) => <stop key={k} offset={k / data.n} stop-color={skyTone(a)} />)}
          </linearGradient>
          <clipPath id={`${id}c`}><rect x="0" y={top} width={W} height={H} rx="7" /></clipPath>
        </defs>
        <rect x="0" y={top} width={W} height={H} rx="7" fill={`url(#${id})`} />
        <g clip-path={`url(#${id}c)`}>
          {best && <rect x={x(best[0])} y={top} width={Math.max(3, x(best[1]) - x(best[0]))} height={H} fill="rgba(239,207,143,0.26)" />}
          {/* Moonlight runs along the foot of the strip while the Moon is up. */}
          {moonRuns.map(([a, b]) => (
            <rect key={a} x={kx(a)} y={top + H - 4} width={Math.max(2, kx(b) - kx(a))} height="4" fill="#efe9dc" opacity={0.35 + 0.55 * lit} />
          ))}
        </g>
        <rect x="0.25" y={top + 0.25} width={W - 0.5} height={H - 0.5} rx="7" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5" />
        {best && <rect x={x(best[0])} y={top - 0.75} width={Math.max(3, x(best[1]) - x(best[0]))} height={H + 1.5} rx="6" fill="none" stroke="var(--accent)" stroke-width="1.5" />}
        {now > from && now < to && (
          <g>
            <line x1={x(now)} x2={x(now)} y1={top - 3} y2={top + H + 3} stroke="#fff" stroke-width="2" stroke-linecap="round" />
          </g>
        )}
      </svg>
      <div class="ribbon-scale tnum">
        <span>{sunset ? <>Sunset <b>{fmtTime(sunset)}</b></> : 'No sunset'}</span>
        <span>{darkStart && darkEnd ? <>Dark <b>{fmtTime(darkStart)}–{fmtTime(darkEnd)}</b></> : 'Never fully dark'}</span>
        <span>{sunrise ? <>Sunrise <b>{fmtTime(sunrise)}</b></> : 'No sunrise'}</span>
      </div>
      <div class="ribbon-key">
        {best && <span><i style={{ background: 'var(--accent)' }} /> Best hours</span>}
        {moonRuns.length > 0 && <span><i style={{ background: '#efe9dc' }} /> Moon up</span>}
        {now > from && now < to && <span><i style={{ background: '#fff', width: '2px', height: '12px' }} /> Now</span>}
      </div>
      {/* Dark is the Sun 18° down, an angle some timetables use for Fajr and ʿIshāʾ: Ahillah gives no prayer times. */}
      <div class="fine">For prayer times, check your local mosque timetable.</div>
    </div>
  );
}

/**
 * How far the orbit data are from the passes shown, when more than three days (a pass may then be a few minutes out):
 * positive when the orbits are older than the night, negative when newer (a night looked back on). Null otherwise.
 */
export function orbitGap(passes: Pass[] | null | undefined, meta: Map<number, { epoch: number }>): number | null {
  for (const p of passes?.slice(0, 12) ?? []) {
    const gap = p.visStart! - (meta.get(p.id)?.epoch ?? p.visStart!);
    if (Math.abs(gap) > FRESH_ORBIT_AGE) return gap;
  }
  return null;
}

function OrbitNote({ gap }: { gap: number | null }) {
  if (gap === null) return null;
  return (
    <div class="muted small pad">
      {gap > 0 ? 'Passes may be a few minutes out until Ahillah updates the satellite orbits.' : 'Passes may be a few minutes out: the satellite orbits Ahillah has were measured more than three days after this night.'}
    </div>
  );
}

const t2d = (t: number) => new Date(t);
function eqHD(body: A.Body, t: number, obs: A.Observer): [number, number] {
  const e = A.Equator(body, t2d(t), obs, true, true);
  return [e.ra, e.dec];
}

const LEVEL = { easy: 'Easy', fair: 'Should show', hard: 'A challenge' } as const;

/** Deep-sky showpieces worth finding tonight with the viewer's own eyes, binoculars or telescope. */
function BestSights({ from, to, openAt }: { from: number; to: number; openAt: (sel: Selection, t: number) => void }) {
  const s = services.value!;
  const set = settings.value;
  const [inst, setInst] = useState<Instrument>(() => loadJSON<{ v?: Instrument }>('instrument', {}).v ?? 'binoculars');
  const [list, setList] = useState<Sight[] | null>(null);
  const obs = s.engine.frame.observer;
  const sqmEye = eyeSqm(place.value, set);
  useEffect(() => {
    setList(null);
    const id = setTimeout(() => setList(bestSights(s.engine.dsos, obs, from, to, inst, set.bortle, sqmEye)), 80);
    return () => clearTimeout(id);
  }, [from, to, inst, set.bortle, sqmEye, obs.latitude, obs.longitude]);
  const choose = (v: Instrument) => {
    setInst(v);
    saveJSON('instrument', { v });
  };
  const [more, setMore] = useState(false);
  return (
    <>
      <h3 class="section-title">Best sights tonight</h3>
      <div class="seg-block">
        <Segmented label="What you will look with" value={inst} options={[['eyes', 'Eyes'], ['binoculars', 'Binoculars'], ['telescope', 'Telescope']]} onChange={choose} />
      </div>
      <div class="results">
        {!list && <div class="empty">Working out what will show from your sky…</div>}
        {list && !list.length && <div class="empty">Nothing faint will show well tonight from here{inst !== 'telescope' ? '; a telescope would help' : ''}. The Moon, planets and bright stars are still worth a look.</div>}
        {list?.slice(0, more ? 10 : 5).map((x) => {
          const d = s.engine.dsos[x.index];
          return (
            <button class="result sight" key={d.id} onClick={() => openAt({ kind: 'dso', index: x.index }, x.t)}>
              {d.m ? <img class="sight-thumb" src={`${import.meta.env.BASE_URL}img/messier/M${d.m}.webp`} alt="" loading="lazy" /> : <span class="result-icon"><Icon name="eye" size={20} /></span>}
              <span class="result-text">
                <span class="result-label">{d.name ?? d.id}{d.name && d.m ? <span class="muted"> · M{d.m}</span> : ''}</span>
                <span class="result-sub"><span class={`sight-level ${x.level}`}>{LEVEL[x.level]}</span> · best {fmtTime(x.t)}, {Math.round(x.alt)}° up in the {directionWords(x.az)} · {d.t.toLowerCase()}</span>
              </span>
              <Icon name="chevron" size={16} />
            </button>
          );
        })}
        {list && list.length > 5 && (
          <button class="show-more" aria-expanded={more} onClick={() => setMore(!more)}>{more ? 'Show fewer' : `Show ${Math.min(10, list.length) - 5} more`}</button>
        )}
      </div>
      {list && list.length > 0 && <div class="fine">Allows for your sky’s brightness, the Moon and height above the horizon. Binoculars means 7×50 or 10×50; telescope means 100 to 150 mm.</div>}
    </>
  );
}
